/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2025, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *           _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Service for managing group units and pinned units.
 */
@Injectable()
export class GroupHttpService {

  /**
   * Holds the name of the currently active tab.
   *
   * Initialized to `null` and updated via `setActiveTab` method.
   */
  activeTab: string = null;

  /**
   * Default Constructor.
   * @param httpClient Http client
   */
  constructor(private httpClient: HttpClient) {
  }

  /**
   * Sets the currently active tab.
   * @param activeTab - The name of the tab to set as active.
   */
  setActiveTab(activeTab: string): void {
    this.activeTab = activeTab;
  }

  /**
   * Fetches all units for a given group.
   *
   * @param groupId - The ID of the group.
   * @returns An observable emitting the list of all units in the group.
   */
  getAllUnits(groupId: string): Observable<any> {
    return this.httpClient.get(`groups/${groupId}`) as Observable<any>;
  }

  /**
   * Fetches all pinned units for a given group.
   *
   * @param groupId - The ID of the group.
   * @returns An observable emitting the list of pinned units.
   */
  getAllPinnedUnits(groupId: string): Observable<any> {
    return this.httpClient.get(`groups/view/${groupId}`) as Observable<any>;
  }

  /**
   * Pins a unit at a given index in a group.
   *
   * @param groupId - The ID of the group.
   * @param index - The position where the unit should be pinned.
   * @param unitId - The ID of the unit to pin.
   * @param pinContent - true if pinned unit is from content tab else false.
   * @returns An observable emitting the updated pinned units status.
   */
  pinUnits(groupId: string, index: number, unitId: string, pinContent: boolean): Observable<any> {
    const params = new HttpParams().set('pinContent', pinContent);
    return this.httpClient.patch(`groups/${groupId}/pin/${index}/${unitId}`, null, { params }) as Observable<any>;
  }

  /**
   * Unpins a unit from a group.
   *
   * @param groupId - The ID of the group.
   * @param unitId - The ID of the unit to unpin.
   * @param pinContent - true if pinned unit is from content tab else false.
   * @returns An observable emitting the updated pinned units status.
   */
  unpinUnits(groupId: string, unitId: string, pinContent: boolean): Observable<any> {
    const params = new HttpParams().set('pinContent', pinContent);
    return this.httpClient.patch(`groups/${groupId}/unpin/${unitId}`, null, { params }) as Observable<any>;
  }

}
